import React from 'react';
import { FormattedMessage } from 'react-intl';

import { TextButton } from '@xingternal/button';
import { Column } from '@xingternal/xing-grid';

import { XingReasonsBanner } from '@/components/_common';
import { FullPageDotLoading } from '@/components/full-page-dot-loading';
import { routes } from '@/config';
import { useBreakpoints, useCrateFormExperiment } from '@/hooks';

import { useApplicationEmail, useIsPrioritizedJob } from '../../contexts';
import { useAccountState, useIsLoggedOutProfileCreationEnabled } from '../../hooks';
import { Feedback } from '../feedback';
import { SubmitConfirmationHeader } from './submit-confirmation-header';
import { SubmitConfirmationTrackingLo } from './submit-confirmation-tracking-lo';
import { AccountState } from './types';
import { useSubmitConfirmationCopies } from './use-submit-confirmation-copies';
import { XingAccountConfirmationVariants } from './xing-account-confirmation-variants';

import * as S from './submit-confirmation.styles';

export const SubmitConfirmationLo: React.VFC<{ onClose: () => void }> = ({ onClose }) => {
  const { isMobile } = useBreakpoints();
  const copies = useSubmitConfirmationCopies();
  const onRegisterClick = (): void =>
    window.location.replace(`${routes.signup}?signup_channel=jobs_apply_form&sc_o=jobs_apply_complete_register`);
  const applicationEmailValue = useApplicationEmail();
  const isLoggedOutProfileCreationEnabled = useIsLoggedOutProfileCreationEnabled();
  const isPaidJob = useIsPrioritizedJob();
  const { isExperimentEnabled } = useCrateFormExperiment(isPaidJob);
  const { accountState, hashedUserId } = useAccountState(applicationEmailValue, isLoggedOutProfileCreationEnabled);

  if (
    isLoggedOutProfileCreationEnabled &&
    (accountState === undefined || (accountState === AccountState.NO_USER && hashedUserId === ''))
  )
    return <FullPageDotLoading />;

  return (
    <>
      <Column size={10} sizeWide={8} offsetWide={2} offset={1}>
        <S.ContentWrapper>
          <SubmitConfirmationHeader copyKey={copies.header} />
          {isLoggedOutProfileCreationEnabled ? (
            <>
              <S.Body size={isMobile ? 'large' : 'xlarge'}>
                <FormattedMessage
                  id="JOBS_INSTANT_APPLY_PROFILE_LO_CONFIRMATION_BODY"
                  values={{ applicationEmailValue, strong: (chunks) => <strong>{chunks}</strong> }}
                />
              </S.Body>
              <XingAccountConfirmationVariants accountState={accountState} hashedUserId={hashedUserId} />
              <TextButton onClick={onClose} size="medium">
                <FormattedMessage id="JOBS_INSTANT_APPLY_PROFILE_BACK_TO_JOBS" />
              </TextButton>
            </>
          ) : (
            <>
              <SubmitConfirmationTrackingLo />
              <S.Body size="medium">
                <FormattedMessage id={copies.body} />
              </S.Body>
              <S.CtaButton variant="primary" size="medium" onClick={onRegisterClick}>
                <FormattedMessage id={copies.register} />
              </S.CtaButton>
              <TextButton onClick={onClose} size="medium">
                <FormattedMessage id={copies.cta} />
              </TextButton>
            </>
          )}
          {isExperimentEnabled ? <Feedback /> : null}
        </S.ContentWrapper>
      </Column>
      {isLoggedOutProfileCreationEnabled ? <XingReasonsBanner /> : null}
    </>
  );
};
